import React from 'react';
import type { ReactElement } from 'react';

import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import * as Sentry from '@sentry/react';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Text } from 'apps/auth/components/common/Text/Text';
import type { TextColor, TextVariant } from 'apps/auth/components/common/Text/Text';
import {
    DELIVERY_BANK_ACCOUNT,
    DELIVERY_SEPA,
    DELIVERY_SWIFT,
    HARD_BLOCK,
    INVALID_DATA,
    REG_NOT_FINISHED,
    SESSION_EXPIRED,
    SOFT_BLOCK,
    TOS_NOT_COMPLIED,
} from 'apps/auth/constants/Constants';
import { POST_CODE } from 'apps/auth/constants/fieldsName';
import { EMAIL, TEL, TEXT as TEXT_FIELD } from 'apps/auth/constants/FieldType';
import { DEFAULT_CURRENCY_ICON, URL_IMAGES_BANKS, URL_IMAGES_CURRENCIES } from 'apps/auth/constants/UrlConstants';
import { CANADA, GUATEMALA, ISRAEL, USA } from 'apps/auth/constants/WellKnownCountries';
import { logInfo, logError } from 'apps/auth/helpers/clientLogger';
import promoColors from 'apps/auth/theme/promoColors';
import type { Country, Language, Nullable, PaymentStatusPayload } from 'apps/auth/types/globals';

type MessageArgs = Array<string | number | undefined> | string | number | Record<string, unknown> | null | undefined;

type JsonDateParts = {
    year: number;
    monthValue: number;
    dayOfMonth: number;
    hour: number;
    minute: number;
    second: number;
    nano: number;
};

type DateInput = string | number | Date;

type RestrictionPerTransaction = {
    maxAmount: number;
};

type ErrorField = {
    code?: number;
    body?: string;
};

type ServerErrors = Record<string, ErrorField> | null | undefined;

type MessageBodyError = Array<{ errorCode?: number | null; value?: string | null }> | null | undefined;

type ChequeErrorPayload = {
    errorCode?: number | null;
    errorMessage?: string | null;
};

type ServerErrorCodePayload = {
    code?: number | null;
    body?: string | null;
};

type HighlightComponent = 'text';

type HighlightStyle = {
    basicColor?: TextColor;
    variant?: TextVariant | null;
    ellipsis?: boolean | null;
    smallCaps?: boolean | null;
};

interface CardExpirationData {
    cardExpired?: string;
    expMonth?: number;
    expYear?: number;
}

type TimerId = ReturnType<typeof setTimeout>;

export const resolveArguments = (message = '', args?: MessageArgs): string => {
    const regex = /\{\d}/g;
    const safeMessage = message ?? '';
    if (Array.isArray(args)) {
        let resolveMessage = safeMessage;
        const matchArray = safeMessage.match(regex);
        if (matchArray) {
            args.forEach((argument, index) => {
                resolveMessage = resolveMessage.replaceAll(`\{${index}\}`, String(argument ?? ''));
            });
            if (matchArray.length !== args.length && !__isDev__) {
                resolveMessage = resolveMessage.replace(regex, '');
            }
        }
        return resolveMessage;
    }
    if (isObject(args)) {
        const matchArray = safeMessage.match(regex);
        if (matchArray && matchArray[0]) {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            const newMessForFirst = safeMessage.replaceAll(`\{0\}`, String(args ?? ''));
            if (matchArray.length > 1 && !__isDev__) {
                return newMessForFirst.replace(regex, '');
            }
            return newMessForFirst;
        }
        return safeMessage;
    }
    return safeMessage;
};

export const removeHtmlTags = (value = ''): string => {
    return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

export const getCurrentLang = (languages: Language[] = [], locale = ''): string => {
    const lang = languages.find((el) => el.localeCode === locale);
    return lang ? lang.name : '';
};

export const getIsoDate = (dateStr: string): string => {
    const dateArr = dateStr.split(dateStr[2]);
    return `${dateArr[2]}-${dateArr[1]}-${dateArr[0]}`;
};

export const getIsoDateUSA = (dateStr: string): string => {
    const dateArr = dateStr.split(dateStr[2]);
    return `${dateArr[2]}-${dateArr[0]}-${dateArr[1]}`;
};

export const getJsDate2 = (jsonDate: Partial<JsonDateParts> = {}): Date => {
    const { year = 0, monthValue = 0, dayOfMonth = 0, hour = 0, minute = 0, second = 0, nano = 0 } = jsonDate;
    const strDate = `${year}-${addZero(monthValue)}-${addZero(dayOfMonth)}T${addZero(hour)}:${addZero(minute)}:${addZero(second)}.${nano}Z`;
    return new Date(strDate);
};

export const getDDMMMYYYYDate = (date: DateInput, lang: string): string => {
    const jsDate = new Date(date);
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: 'short' })} ${addZero(jsDate.getFullYear())}`;
};

export const getDDMMYYYYNumericDate = (date: Date, country: number | string): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return country === USA ? `${month}/${day}/${year}` : `${day}.${month}.${year}`;
};

export const getDayMonthDate = (date: string, lang: string, length: Intl.DateTimeFormatOptions['month']): string => {
    const jsDate = new Date(date?.replace(/(\d{2}).(\d{2}).(\d{4})/, '$2/$1/$3'));
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: length })}`;
};

export const isDateToday = (date: DateInput): boolean => {
    const dateToCheck = new Date(date);
    const today = new Date();
    return dateToCheck.getDate() === today.getDate() && dateToCheck.getMonth() === today.getMonth() && dateToCheck.getFullYear() === today.getFullYear();
};

export const addZero = (value: number): string => (value < 10 ? `0${value}` : String(value));

export const getDeltaSecondsBetweenDates = (dateStart: Date, dateEnd: Date): number => {
    const mStart = dateStart.getTime();
    const mEnd = dateEnd.getTime();
    return ((mEnd - mStart) / 60) * 1000;
};

const getNavigatorLangSuffix = (): string => {
    if (typeof window === 'undefined') {
        return '';
    }
    return window.navigator?.language?.substring(2) ?? '';
};

export const getGaActivityDate = (dateTime: string, lang: string): string => {
    const jsDate = new Date(`${dateTime}Z`);
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: 'short' }).replace('.', '')} · ${jsDate.toLocaleString(lang, {
        weekday: 'short',
    })} ${addZero(jsDate.getHours())}:${addZero(jsDate.getMinutes())}`;
};

export const getLimitEndDate = (date: DateInput, lang: string): string => {
    return new Intl.DateTimeFormat(`${lang}${getNavigatorLangSuffix()}`, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(date));
};

export const getHistoryTransactionDate = (date: DateInput, lang: string): string => {
    const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
    };
    return new Intl.DateTimeFormat(`${lang}${getNavigatorLangSuffix()}`, options).format(new Date(date));
};

export const getHistoryTransactionFullDate = (
    date: DateInput,
    lang: string,
    userCountry?: number | string | Country | null,
): { formattedDate: string; formattedTime: string } => {
    const isUSAFormat = (typeof userCountry === 'object' ? userCountry?.id : userCountry) === USA && lang === 'en';
    const locale = isUSAFormat ? 'en-US' : `${lang}${getNavigatorLangSuffix()}`;

    const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };

    const formattedDate = new Intl.DateTimeFormat(locale, dateOptions).format(new Date(date));
    const formattedTime = new Intl.DateTimeFormat(locale, timeOptions).format(new Date(date));

    return {
        formattedDate,
        formattedTime,
    };
};

export const getDDMonEEEHHmmDate = (dateTime: DateInput | undefined, lang: string): string | undefined => {
    if (dateTime) {
        const jsDate = new Date(dateTime);
        return `${jsDate.getDate()} ${jsDate.toLocaleString(lang, { month: 'short' }).replace('.', '')} · ${jsDate.toLocaleString(lang, {
            weekday: 'short',
        })} ${addZero(jsDate.getHours())}:${addZero(jsDate.getMinutes())}`;
    }
    return undefined;
};

export const getGMTdate = (dateTime: DateInput | JsonDateParts | undefined | null): string | undefined => {
    if (dateTime === undefined || dateTime === null) return undefined;
    const jsDate = typeof dateTime === 'object' && !(dateTime instanceof Date) ? getJsDate2(dateTime) : new Date(dateTime);
    const h = Math.floor(Math.abs(jsDate.getTimezoneOffset()) / 60);
    const gmt = (jsDate.getTimezoneOffset() < 0 ? '+' : '-') + addZero(h) + ':' + addZero(Math.abs(jsDate.getTimezoneOffset()) - h * 60);
    return `${addZero(jsDate.getDate())}.${addZero(jsDate.getMonth() + 1)}.${jsDate.getFullYear()}, ${addZero(jsDate.getHours())}:${addZero(
        jsDate.getMinutes(),
    )} (GMT${gmt})`;
};

export const timer = (timerID: TimerId | null, callFun: () => void, delay = 300): TimerId => {
    if (timerID) {
        clearTimeout(timerID);
    }
    const newTimer = setTimeout(() => {
        callFun();
    }, delay);
    return newTimer;
};

export const isObject = (obj: unknown): obj is Record<string, unknown> | Array<unknown> | string | number => {
    return obj !== null && typeof obj !== 'undefined';
};

export const validOnMod10 = (cardNumber: string): boolean => {
    const arr = cardNumber.split('').map((char, index) => {
        const digit = parseInt(char, 10);
        if ((index + cardNumber.length) % 2 === 0) {
            const digitX2 = digit * 2;
            return digitX2 > 9 ? digitX2 - 9 : digitX2;
        }
        return digit;
    });
    return !(
        arr.reduce((a, b) => {
            return a + b;
        }, 0) % 10
    );
};

export const random128Hex = (): string => {
    const random16Hex = () => {
        return (0x10000 | (Math.random() * 0x10000)).toString(16).substr(1);
    };
    return (
        random16Hex() + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + random16Hex() + random16Hex()
    );
};

export const getLangCountryPrefix = (lang?: string | null, country?: string | null): string => {
    let prefix = '/';
    if (lang && typeof country === 'string') {
        prefix = `/${lang}-${country.toLowerCase()}`;
    } else if (lang) {
        prefix = `/${lang}`;
    }
    return prefix;
};

export const placeSrcSetAttr = (img1?: string | null, img2?: string | null): string | undefined => {
    if (img1 && img2) return `${img1} 1x, ${img2} 2x`;
    if (img1 && !img2) return `${img1} 2x`;
    return undefined;
};

export const isSpecialCountry = (countryId?: number | string | null): boolean => {
    return countryId === USA || countryId === CANADA;
};

export const getExtra = (extra: string | null | undefined, extraName: string): string | null => {
    if (extra && typeof extra === 'string') {
        const extras = extra.split(';');
        const filtered = extras.filter((e) => e && e.split('=')[0] === extraName);
        return filtered && filtered[0] ? filtered[0].split('=')[1] : null;
    }
    return null;
};

export const toLowerCase = (str: string | null | undefined): string => {
    return str && typeof str === 'string' ? str.toLowerCase() : '';
};

export const toUpperCase = (str: string | null | undefined): string => {
    return str && typeof str === 'string' ? str.toUpperCase() : '';
};

export const getEmptyStringIfNull = (str: string | null | undefined): string | null => {
    return str === null ? '' : (str ?? null);
};

export const isNotEmptyArray = (arr: unknown): arr is Array<unknown> => Array.isArray(arr) && arr.length > 0;

export const canShareDetails = (): boolean => {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
};

export const shareDetails = (title: string, text: string): void => {
    if (canShareDetails()) {
        navigator
            .share({
                title,
                text,
            })
            .then(() => {
                logInfo('Successfully shared');
            })
            .catch((error) => {
                logError('Something went wrong sharing the block', error);
            });
    }
};

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
    if (typeof document === 'undefined') {
        return false;
    }
    const textArea = document.createElement('textarea');
    textArea.style.background = 'transparent';
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.value = text;

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
        document.body.removeChild(textArea);
        return false;
    }

    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        Sentry.captureException(err, {
            level: 'info',
        });
        logInfo('Oops, unable to copy');
        return false;
    } finally {
        document.body.removeChild(textArea);
    }

    return true;
};

export const getBankAlias = (alias?: string | null, iconName = 'card', isDisabled?: boolean): ReactElement => {
    const logoSymbol = <Icon name={iconName} size="24px" />;
    return (
        <DiskBadge
            variant={alias ? 'hasBorderedImg' : 'tertiary'}
            src={alias ? `${URL_IMAGES_BANKS}/${alias}.png` : undefined}
            iconName={!alias ? iconName : undefined}
            isDisabled={isDisabled}
            isInactive={false}
            logoSymbol={logoSymbol}
        />
    );
};

export const deletingWhiteSpaceInString = (str: string | null | undefined): string | null | undefined => {
    if (typeof str === 'string') {
        const array = str.match(/(\d+)\s([\d\.]+)/) || [];
        if (array.length === 0) {
            return str;
        }
        return array[1] + array[2];
    }
    return str;
};

export const deleteArtifactsFromStr = (str: string): string => {
    const regex = /\s*{.*}/;
    return str.replace(regex, '');
};

export const clabeCalcChecksum = (clabeNum?: string): boolean => {
    if (typeof clabeNum === 'undefined' || clabeNum.length !== 18) {
        return false;
    }
    let sum = 0;

    function add(digit: string, index: number) {
        sum += (parseInt(digit, 10) * [3, 7, 1][index % 3]) % 10;
    }

    clabeNum.split('').slice(0, 17).forEach(add);
    return parseInt(clabeNum.substring(17, 18), 10) === (10 - (sum % 10)) % 10;
};

export const isCardExpired = (card?: CardExpirationData | null): boolean => {
    let month: number | undefined;
    let year: number | undefined;
    const now = new Date();
    const currentYear = parseInt(now.getFullYear().toString().substr(-2), 10);
    const currentMonth = now.getMonth() + 1;
    if (card?.cardExpired) {
        month = parseInt(card.cardExpired.split('/')[0], 10);
        year = parseInt(card.cardExpired.split('/')[1], 10);
    } else if (card?.expMonth && card?.expYear) {
        month = card.expMonth;
        year = card.expYear;
    }
    if (typeof year === 'undefined' || typeof month === 'undefined') {
        return false;
    }
    return year < currentYear || (currentYear === year && month < currentMonth);
};

export const getFormattedCardOrAccountNumber = (number: string | null | undefined, deliveryMethod?: string): string | null | undefined => {
    if (number != null && (deliveryMethod === DELIVERY_BANK_ACCOUNT || deliveryMethod === DELIVERY_SWIFT || deliveryMethod === DELIVERY_SEPA)) {
        const formattedNumberArr = [] as string[];
        for (let i = 0; i < number.length; i += 4) {
            formattedNumberArr.push(number.substring(i, i + 4));
        }
        return formattedNumberArr.join(' ');
    }
    return number;
};

export const getSortCodeWithHyphens = (sortCode?: string | null): string | null => {
    if (typeof sortCode === 'string' && sortCode.length > 0) {
        const formattedSortCodeArr = [] as string[];
        for (let i = 0; i < sortCode.length; i += 2) {
            formattedSortCodeArr.push(sortCode.substring(i, i + 2));
        }
        return formattedSortCodeArr.join('-');
    }
    return null;
};

export const getReferenceCodeWithHyphen = (referenceCode?: string | null): string | null => {
    if (typeof referenceCode === 'string' && referenceCode.length > 0) {
        return referenceCode.replace(/^(\d{3})(\d{8})$/, '$1-$2');
    }
    return null;
};

export const getFormattedPhoneNumber = (number: string, country: number | string): string => {
    if (country === GUATEMALA) {
        const format = number.match(/^(\d{3})(\d{4})(\d{4})$/);
        if (format) {
            return `${format[1]} ${format[2]} ${format[3]}`;
        }
    }
    return number;
};

export const getActualMaxAmountFrom = (
    userCountry: Nullable<Country>,
    leftLimitAmount: number | null | undefined,
    canMakeDraftTransaction: boolean,
    restrictionPerTransaction: RestrictionPerTransaction,
): number | null | undefined => {
    if (userCountry?.id === ISRAEL) {
        return restrictionPerTransaction.maxAmount;
    }

    return leftLimitAmount && !canMakeDraftTransaction && leftLimitAmount < restrictionPerTransaction.maxAmount
        ? leftLimitAmount
        : restrictionPerTransaction.maxAmount;
};

export const findErrorCodeAndMessage = (
    messageBodyError: MessageBodyError,
    serverErrors: ServerErrors,
    cheque: ChequeErrorPayload | null | undefined,
    paymentStatus: PaymentStatusPayload | null | undefined,
    serverErrorCode: ServerErrorCodePayload | null | undefined,
): { errorCode: number | string | null; errorMessage: string | null } => {
    let errorCode: number | string | null = null;
    let errorMessage: string | null = null;
    if (messageBodyError?.length) {
        messageBodyError.forEach((error) => {
            if (typeof error?.errorCode !== 'undefined') {
                errorCode = error.errorCode ?? null;
            }
            errorMessage = error?.value ?? null;
        });
    } else if (serverErrors && Object.keys(serverErrors).length > 0) {
        Object.values(serverErrors).forEach((errorField) => {
            if (typeof errorField?.code !== 'undefined') {
                errorCode = errorField.code ?? null;
            }
            errorMessage = errorField?.body ?? null;
        });
    } else if (cheque?.errorCode) {
        errorCode = cheque.errorCode;
        errorMessage = cheque.errorMessage ?? null;
    } else if (paymentStatus?.errorCode) {
        errorCode = paymentStatus.errorCode;
        errorMessage = paymentStatus.message ?? null;
    } else if (serverErrorCode?.code) {
        errorCode = serverErrorCode.code;
        errorMessage = serverErrorCode.body ?? null;
    }
    return { errorCode, errorMessage };
};

const softBlockErrors = [
    160, 611, 612, 613, 615, 616, 617, 618, 619, 620, 621, 622, 631, 632, 634, 635, 636, 637, 638, 639, 640, 641, 642, 644, 649, 656, 6001, 6002, 6003, 6005,
    6006, 6007, 6008, 6010, 6011, 6012, 6013, 6014, 6015, 6016, 6017, 6018, 6019, 6020, 6021, 6022, 6023, 6024, 6025, 6026, 6027, 6028, 6029, 6030, 6031, 6032,
    6033, 6034, 6035, 6036, 6037, 6038, 6039, 6040, 6041, 6042, 6043, 6044, 6045, 6046, 6047, 6048, 6049, 6050, 6051, 6052, 6054, 6055, 6056, 6057, 6063, 6064,
    6065,
];
const hardBlockErrors = [150, 153, 655, 6059, 6060, 6061, 6062, 6099];

export const hardBlockErrorType = (errorCode?: number | string | null): string | undefined => {
    switch (errorCode) {
        case 655:
        case 6059:
        case 6060:
        case 6061:
        case 6062:
            return TOS_NOT_COMPLIED;
        case 6099:
            return REG_NOT_FINISHED;
        case 153:
            return SESSION_EXPIRED;
        case 150:
            return INVALID_DATA;
        default:
            return undefined;
    }
};
export const showErrorType = (
    messageBodyError: MessageBodyError,
    serverErrors: ServerErrors,
    chequeError?: ChequeErrorPayload | null,
    paymentStatusError?: PaymentStatusPayload | null,
    serverErrorCode?: ServerErrorCodePayload | null,
): string | undefined => {
    const { errorCode } = findErrorCodeAndMessage(messageBodyError, serverErrors, chequeError, paymentStatusError, serverErrorCode);
    let errorType;
    if (typeof errorCode === 'number' && softBlockErrors.includes(errorCode)) {
        errorType = SOFT_BLOCK;
    } else if (typeof errorCode === 'number' && hardBlockErrors.includes(errorCode)) {
        errorType = HARD_BLOCK;
    }
    return errorType;
};

export const isSoftOrHardServerError = (error?: number | null): boolean => {
    if (typeof error !== 'number') {
        return false;
    }
    return softBlockErrors.includes(error) || hardBlockErrors.includes(error);
};

export const getCurrencyIcon = (name: string): string => {
    return `${URL_IMAGES_CURRENCIES}/currency-${name}.svg`;
};
export const onIconRenderError = (event: React.SyntheticEvent<HTMLImageElement>): void => {
    const target = event.target as HTMLImageElement;
    target.onerror = null;
    target.src = DEFAULT_CURRENCY_ICON;
};
export const capitalizeFirstLetters = (str?: string | null): string => {
    if (str && typeof str === 'string') {
        const arr = str.toLowerCase().split(' ');
        return arr.map((item) => item[0].toUpperCase() + item.substring(1)).join(' ');
    }
    return '';
};
export const getReference = (referenceNumber?: string | null, paymentId?: string | null): string | null => {
    const reference = referenceNumber || paymentId;
    return getReferenceCodeWithHyphen(reference ?? undefined);
};

export const UUID = (): string => {
    const lut: string[] = [];
    for (let i = 0; i < 256; i++) {
        lut[i] = (i < 16 ? '0' : '') + i.toString(16);
    }

    const d0 = (Math.random() * 0xffffffff) | 0;
    const d1 = (Math.random() * 0xffffffff) | 0;
    const d2 = (Math.random() * 0xffffffff) | 0;
    const d3 = (Math.random() * 0xffffffff) | 0;
    return (
        lut[d0 & 0xff] +
        lut[(d0 >> 8) & 0xff] +
        lut[(d0 >> 16) & 0xff] +
        lut[(d0 >> 24) & 0xff] +
        '-' +
        lut[d1 & 0xff] +
        lut[(d1 >> 8) & 0xff] +
        '-' +
        lut[((d1 >> 16) & 0x0f) | 0x40] +
        lut[(d1 >> 24) & 0xff] +
        '-' +
        lut[(d2 & 0x3f) | 0x80] +
        lut[(d2 >> 8) & 0xff] +
        '-' +
        lut[(d2 >> 16) & 0xff] +
        lut[(d2 >> 24) & 0xff] +
        lut[d3 & 0xff] +
        lut[(d3 >> 8) & 0xff] +
        lut[(d3 >> 16) & 0xff] +
        lut[(d3 >> 24) & 0xff]
    );
};

export const getRepeatQueryParams = (): { repeatId: string | null; recipientId: string | null } => {
    let repeatId: string | null = null;
    let recipientId: string | null = null;
    if (__isBrowser__) {
        const url = new URL(window.location.href);
        repeatId = url.searchParams.get('repeatId');
        recipientId = url.searchParams.get('recipientId');
    }
    return { repeatId, recipientId };
};

export const getCurrentSystemTheme = (): 'light' | 'dark' => {
    let theme: 'light' | 'dark' = 'light';
    if (typeof window !== 'undefined' && window?.matchMedia) {
        const isMatchesDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
        theme = isMatchesDark ? 'dark' : 'light';
    }
    return theme;
};

export const focusFirstInput = (): void => {
    if (typeof document === 'undefined') {
        return;
    }
    const inputs = document.querySelectorAll('input');
    const types: string[] = [TEXT_FIELD, TEL, EMAIL];

    if (inputs.length > 0) {
        const firstInput = inputs[0];
        const isValidInput = types.includes(firstInput.type) && !firstInput.readOnly && firstInput.offsetParent !== null && firstInput.id !== POST_CODE;
        if (isValidInput) {
            firstInput.focus();
        }
    }
};

const escapeRegExp = (value: string): string => {
    return value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};

const getThemePurple = (theme: DefaultTheme): string => {
    const typedTheme = theme as DefaultTheme & { purple_100?: string };
    return typedTheme.purple_100 ?? promoColors.primary;
};

const Highlight = styled.span`
    color: ${(props) => getThemePurple(props.theme)};
`;

export const getHighlightedText = (
    query: string,
    text: string | null | undefined,
    _componentType: HighlightComponent = 'text',
    style: HighlightStyle = {},
    _useThemeColor = false,
    isDisabled = false,
): ReactElement => {
    const escapedQuery = escapeRegExp(query);
    const safeText = text ?? '';
    const parts = safeText.split(new RegExp(`(${escapedQuery})`, 'gi'));
    const textColor: TextColor = style.basicColor ?? 'primary';
    const textVariant = style.variant ?? undefined;
    const textProps = {
        color: isDisabled ? 'lightGray' : textColor,
        variant: textVariant,
        ellipsis: style.ellipsis ?? undefined,
        smallCaps: style.smallCaps ?? undefined,
    };

    return (
        <Text {...textProps}>
            {parts.map((part, i) =>
                part.toLowerCase() === query.toLowerCase() ? (
                    <Highlight key={`${safeText}-${i}`}>{part}</Highlight>
                ) : (
                    <React.Fragment key={`${safeText}-${i}`}>{part}</React.Fragment>
                ),
            )}
        </Text>
    );
};
